/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'jquery',
	'../../../widgets/livewidget/nls/StringResources',
	'../../../lib/@waca/dashboard-common/dist/utils/FlyoutContentBase',
	'doT',
	'text!./templates/ForecastOptionsList.html',
	'react-dom',
	'react',
	'ca-ui-toolkit',
	'underscore'
], function($, stringResources, FlyoutContentBase, dot, ForecastOptionsList, ReactDOM, React, Toolkit, _) {
	'use strict';
	/* eslint react/prop-types: 0 */ // TODO: Remove once we have prop-types brought in from glass.

	const ForecastOptionsListTemplate = dot.template(ForecastOptionsList);

	const {
		ToggleSwitch,
		Label,
		Tooltip,
		TextInput,
		Select,
		keyboardUtils
	} = Toolkit;

	const { isKey, keyNames } = keyboardUtils;

	function TitleDiv(props) {
		const label = stringResources.get('forecast_title');
		let toggle = <ToggleSwitch
			className='forecastTitleToggle'
			checked={props.toggleEnabled}
			onChange={props.onToggleChange}
			small={true}
			align='right'
		/>;
		const toggleTooltipLabel = props.toggleEnabled ? stringResources.get('forecast_toggle_off') : stringResources.get('forecast_toggle_on');
		let toggleSection = toggle ? <Tooltip title={toggleTooltipLabel} orient='top'>
			{toggle}
		</Tooltip> : null;
		return (
			<div className='forecastWrapperDiv'>
				<div className='forecastTitleDiv'>
					<Tooltip title={label} orient='top'>
						<Label
							className='forecastTitleLabel'
							label={label}
						/>
					</Tooltip>
					{toggleSection}
				</div>
			</div>
		);
	}

	class PredictComponents extends React.Component {
		constructor(props) {
			super(props);
			let clearedSettings = this._clearValidationErrorForAllSettings(_.clone(props.settings));
			this.state = {
				settings: clearedSettings
			};
		}

		/**
		* Validation method for Number input
		* @param {String} value - value entered by the user
		* @param {String} id - id of the item being changed.
		* return {Boolean} true iff value is a number and within a specified range.
		*/
		validateNumberInput(value, id) {
			if (_.isEmpty(value)) { return true; }
			const valueAsNum = Number(value);
			if (!_.isNumber(valueAsNum)) {
				return false;
			}
			const item = _.find(this.state.settings, item => {return item.id === id;});
			const min = item.restrictions.minimum;
			const max = item.restrictions.maximum;
			return (min <= valueAsNum  && valueAsNum <= max);
		}

		_clearValidationErrorForAllSettings(settings) {
			// If we are blurring then we know the value is valid, ensure the state is seen that way.
			let newSettings = _.clone(settings);
			_.each(newSettings, (item) => {
				item.showValidationError = false;
			});
			return newSettings;
		}

		/**
		* Ensure the value is valid input. If not, set the error state.
		* @param {Object} item of interest
		* @param {String} value entered by a user, checked to be a valid number or null
		*/
		_validateNumberInputForItem(item, value) {
			const isValid = this.validateNumberInput(value, item.id);
			if (!isValid) {
				item.showValidationError = true;
				this.setState({ validationError: true });
			}
			return isValid;
		}

		/**
		* If the value is valid for the item, clear the state error, set the state and the model.
		* @param {Object} item of interest
		* @param {String} value entered by a user, checked to be a valid number or null
		*/
		_checkAndSetNumberValues(item, value) {
			if(this._validateNumberInputForItem(item, value)) {
				// If we are blurring then we know the value is valid, ensure the state is seen that way.
				let clearedSettings = this._clearValidationErrorForAllSettings(this.state.settings);
				this.setState({
					predictItems: clearedSettings,
					validationError: false
				});
				this.props.onForecastPredictItemsChanged(clearedSettings);
			}
		}

		onBlur(item, value) {
			// Ensure the value is valid before setting the state and the model
			this._checkAndSetNumberValues(item, value);
		}

		_getNumberReactComponent(item) {
			const valueTooltip = item.showValidationError ? 'Enter a number between ' + item.restrictions.minimum + ' and ' + item.restrictions.maximum : item.value;
			return <div className='forecastPredictInput' key={item.id}>
				<Tooltip title={item.description} orient='top'>
					<Label
						type='caption'
						htmlFor={item.id}
						label={item.name}
						className='predictForecastTitle'
					/>
				</Tooltip>
				<TextInput
					className='predictForecastOptionEntry'
					id={item.id}
					title={valueTooltip}
					value={item.value}
					disabled={this.props.isDisabled}
					placeholder = {item.default}
					onValueChange={(value, e) => this._applyNumberValuesToState(value, item, e)}
					fireOnBlur={true}
					onBlur={(e) => this.onBlur(item, e.target.value)}
					allowInvalidEntry
					hasValidationError={item.showValidationError}
					showIsReqWhenValidErrTxt = {false} // Don't show the error message as its messy.
					onValueChangeInvalid={() => {item.showValidationError = true; this.setState({ validationError: true });} }
					validEntryMatch={v => { return this.validateNumberInput(v, item.id); }}
				/>
			</div>;
		}

		_updateStateClearErrorsGetNewPredictItems(val, item) {
			const newPredictValues = _.clone(this.state.settings);

			newPredictValues.forEach(predictItem => {
				if(predictItem.id === item.id){
					predictItem.showValidationError = false;
					if (_.isEmpty(val)) {
						delete predictItem.value;
					} else {
						// hack for now due to diff formats btw val and ui component used (text input for ints)
						predictItem.value = item.id === 'confidenceLevel' ? val : parseInt(val);
					}
				}
			});

			this.setState({
				predictItems: newPredictValues,
				validationError: false
			});
			return newPredictValues;
		}

		/**
		* Handle the number input changes
		* @param {Object} value - value being selected.
		* @param {String} id - id of the predict item being modified
		* @param {Object} e - Object describing the event target.
		*/
		_applyNumberValuesToState(val, item, e){
			if(this._validateNumberInputForItem(item, val)) {
				const newPredictValues = this._updateStateClearErrorsGetNewPredictItems(val, item);
				// If the user hit enter then update the model.
				if (e.type === 'keydown' && isKey(e, keyNames.ENTER)) {
					this.props.onForecastPredictItemsChanged(newPredictValues);
				}
			}
		}

		/**
		* Handle the input changes
		* @param {Object} value - value being selected.
		* @param {String} id - id of the predict item being modified
		*/
		_applySelectValuesToState(val, item) {
			const newPredictValues = this._updateStateClearErrorsGetNewPredictItems(val, item);
			this.props.onForecastPredictItemsChanged(newPredictValues);
		}

		_getEnumerationReactComponent(item) {
			let values = [];
			item.restrictions.values.map(item => {
				values.push({
					value:item.value,
					label: item.name
				});
			});
			const selectedValue = item.value || item.default;
			return <div className='forecastPredictInput' key={item.id}>
				<Tooltip title={item.description} orient='top'>
					<Label
						type='caption'
						htmlFor={item.id}
						label={item.name}
						className='predictForecastTitle'
					/>
				</Tooltip>
				<Select
					id={item.id}
					selected={selectedValue}
					disabled={this.props.isDisabled}
					onChange={value => this._applySelectValuesToState(value, item)}
					options={values}
					className='predictForecastOptionEntry'
				/>
			</div>;
		}
		render() {
			const predictItems = this.state.settings.map(item => {
				if (item.restrictions.type === 'enumeration') {
					return this._getEnumerationReactComponent(item);
				} else {
					return this._getNumberReactComponent(item);
				}
			});
			return(
				<div className='predictForecastOptions'>
					{predictItems}
				</div>
			);
		}
	}

	class ReactComponents extends React.Component {

		constructor(props) {
			super(props);
			this.state = {
				toggleEnabled: this.props.initialState.toggleEnabled,
				settings: this.props.initialState.settings
			};
		}
		_updateForecastState(newState) {
			this.props.onForecastValuesStateChanged(_.clone(newState));
		}

		/*
		* Toggle switch state changed.
		*/
		onToggleChange() {
			// Copy the current state, making the needed change, then set the state to cause a re-render
			const newState = _.extend({}, this.state, { toggleEnabled: !this.state.toggleEnabled });
			this.setState(newState);

			// Update the parent to set the state in the model
			this._updateForecastState(newState);
		}
		predictSettingsChanged(settings) {
			// Copy the current state, making the needed change, then set the state to cause a re-render
			const newState = _.extend({}, this.state, { settings: settings });

			// Update the parent to set the state in the model
			this._updateForecastState(newState);
		}
		render() {
			return (
				<div className='forecastComponents'>
					<div className='ba-theme-waca' role='region' aria-label='{{!it.title}}'>
						<TitleDiv
							onToggleChange={() => this.onToggleChange()}
							toggleEnabled={this.state.toggleEnabled}
						/>
						<PredictComponents
							settings = {this.state.settings}
							isDisabled={!this.state.toggleEnabled}
							onForecastPredictItemsChanged = {predictItems => this.predictSettingsChanged(predictItems)}
						/>
					</div>
				</div>
			);
		}
	}

	const ForecastView = FlyoutContentBase.extend({
		init: function(options) {
			ForecastView.inherited('init', this, arguments);
			this.visAPI = options.viewOptions.visAPI;
			this.uniqueId = options.viewOptions.uniqueId;
			this.popOver = null;
			this.markup = ForecastOptionsListTemplate({
				'title': 'Forecast'
			});
			this.content = options.viewOptions.content;
		},

		setFocus: function() {
			this.popOver = $('.forecastPopover.' + this.uniqueId);
			this.popOver.find('input:first').focus();
		},

		_onRemove() {

			// if a popOver has not been created, do not try to remove it
			if (this.popOver) {
				// We need to unmount the react components so that, when the popup is re-opened
				// React knows that the state has changed and will actually render the
				// components again.
				const popoverContent = this.popOver.find('.popover-content')[0];
				if ($(popoverContent).find('.forecastComponents').length > 0) {
					ReactDOM.unmountComponentAtNode(popoverContent);
				}

				// Popover needs to be removed as well.
				this.popOver.remove();
			}
			this.popupShown = false;
			this.reactComponents = null;
		},

		remove: function() {
			ForecastView.inherited('remove', this, arguments);
			this._onRemove();
		},

		//@override
		onPopupClosed: function() {
			this._onRemove();
		},

		/* TODO: React needs to have a dom element to render, flyouts work by
		* asking for rendered html. For now render a basic template and then
		* when the popup is shown add the react components.
		*/
		getRenderedHtml: function() {
			// If the indicator is selected while the popover is open the flyout code
			// acts stupid and tries to re-render from scratch, which means getting
			// the base html and tossing the old popover. We need to unmount the React
			// components in this case.
			if (this.popOver && this.popOver.find('.popover-content')[0]) {
				const popoverContent = this.popOver.find('.popover-content')[0];
				if ($(popoverContent).find('.forecastComponents').length > 0) {
					ReactDOM.unmountComponentAtNode(popoverContent);
				}
			}
			return this.markup;
		},

		onPopupShown: function() {
			// Find the anchor node (i.e. the popover content)
			const popOverDom = this.popOver.find('.popover-content')[0];
			// If the popup isn't shown build it
			if (!this.popupShown) {
				let initialState = _.clone(this.content.getFeature('Forecast').getForecastDisplayState());
				this.popupShown = true;

				// Render the react components
				this.reactComponents = ReactDOM.render(<ReactComponents
					initialState={initialState}
					onForecastValuesStateChanged={state=>this.onForecastValuesStateChanged(state)}
				/>,
				popOverDom
				);
				this.setFocus();
			}
		},

		onForecastValuesStateChanged(state) {
			this.content.getFeature('Forecast').applyStateChange(state);
		}
	});
	return ForecastView;
});
